import {useQuery} from '@apollo/client';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {sanitiseProductType} from '../../../utils/sanitiseProductType';
import validateUUID from '../../../utils/validateUUID';
import {BUILDER_TRANSFER_POTS} from '../builderPotQueries';
import {reccNonEditable} from '../CreateBuilder/BuilderSourceDestination/CreateBuilderFooter';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {BuilderPot} from '../../../schema-types/BuilderPot';
import {
  BuilderTransferPots,
  BuilderTransferPotsVariables,
} from '../../../schema-types/BuilderTransferPots';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';

export const useTransferPotsQuery = (): {
  sourcePots: BuilderPot[];
  destinationPots: BuilderPot[];
} => {
  const {builderDetails, builderReviewId} = useBuilderContext();
  const {values} = useAutoForm<builderFieldsType>();
  const contactId = values.contact ? validateUUID(values.contact) || null : null;
  const isNonEditable = !!reccNonEditable(builderDetails);

  const {data: potsData} = useQuery<BuilderTransferPots, BuilderTransferPotsVariables>(
    BUILDER_TRANSFER_POTS,
    {
      variables: {
        reviewId: validateUUID(builderReviewId)!,
        contactId,
        potType: sanitiseProductType(values.mainPlanType) ?? '',
      },
      skip:
        isNonEditable ||
        values.planAction !== OVRecommendActionEnum.TRANSFER ||
        !values.mainPlanType ||
        !validateUUID(builderReviewId),
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }
  );

  if (isNonEditable) {
    const savedSources =
      builderDetails?.transfer?.existingSources
        ?.map((source) => source?.sourcePot)
        .filter((pot): pot is NonNullable<typeof pot> => !!pot) ?? [];
    const savedDestination = builderDetails?.transfer?.destinationPot;
    return {
      sourcePots: savedSources,
      destinationPots: savedDestination ? [savedDestination] : [],
    };
  }

  return {
    sourcePots: potsData?.transferPots?.sources ?? [],
    destinationPots: potsData?.transferPots?.destinations ?? [],
  };
};
